import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";

/** Provider marks a booking complete + optionally picks a rebook window
 * (2–12 weeks) so the customer gets a one-tap rebook push when due. */
export default function CompleteBookingDialog({ open, booking, onClose, onDone }) {
  const [weeks, setWeeks] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setWeeks(0);
  }, [open]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.id}/complete`, { rebook_weeks: weeks });
      toast.success(weeks ? `Marked complete — rebook reminder set for ${weeks} weeks out` : "Marked complete");
      onDone();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!booking) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="complete-booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-spectral">Mark complete</DialogTitle>
          <DialogDescription className="text-[12px]">
            Wrap up this booking and optionally schedule a rebook reminder.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-lg border border-[#E8D9A6] bg-[#FBF7EC]/50 p-3">
          <Label className="text-xs font-semibold">Suggest their next visit? (optional)</Label>
          <select
            className="mt-1 w-full rounded-md border border-[#E5E5E5] bg-white px-3 py-2 text-sm"
            value={weeks}
            onChange={(e) => setWeeks(parseInt(e.target.value, 10))}
            data-testid="complete-rebook-weeks"
          >
            <option value={0}>No reminder</option>
            {[2, 3, 4, 5, 6, 8, 10, 12].map((w) => (
              <option key={w} value={w}>In {w} weeks</option>
            ))}
          </select>
          <p className="text-[11px] text-[#6B6B6B] mt-1.5">
            They'll get a friendly "time to rebook" push with a one-tap link back to you.
          </p>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} className="rounded-full bg-ink hover:bg-crimson text-white w-full" data-testid="complete-booking-submit">
            {busy ? "Saving…" : "Mark complete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
